import React, { useEffect, useState } from "react";
import api from "../api/axios";
import "./AdminApprovalsPage.responsive.css";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "user",
  speciality: "",
  sub_speciality: "",
  degree: "",
  license_id: "",
  experience_years: "",
  organization_name: "",
  provider_type: "",
  verification_document_url: "",
  verification_notes: "",
  city: "",
  state: "",
  country: "",
};

export default function AdminApprovalsPage() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);

  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get("/api/admin/users", {
        params: {
          search: search || undefined,
          role: role || undefined,
          status: status || undefined,
          page,
          page_size: pageSize,
        },
      });

      setUsers(res.data.items || []);
      setTotalUsers(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      const res = await api.get("/api/admin/approvals/pending");
      setPendingUsers(res.data || []);
    } catch {
      setPendingUsers([]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadPendingApprovals();
  }, [page, role, status]);

  const handleSearch = () => {
    setPage(1);
    loadUsers();
  };

  const refreshAll = async () => {
    await loadUsers();
    await loadPendingApprovals();
  };

  const approveUser = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/approve`);
      await refreshAll();
      setSelectedUser(null);
    } catch (err) {
      alert(err?.response?.data?.detail || "Approval failed");
    }
  };

  const rejectUser = async (userId) => {
    const reason = window.prompt("Reason for rejection?");
    if (!reason) return;

    try {
      await api.post(`/api/admin/users/${userId}/reject`, { reason });
      await refreshAll();
      setSelectedUser(null);
    } catch (err) {
      alert(err?.response?.data?.detail || "Reject failed");
    }
  };

  const suspendUser = async (userId) => {
    const reason = window.prompt("Reason for restriction/suspension?", "Restricted by admin");
    if (!reason) return;

    try {
      await api.post(`/api/admin/users/${userId}/suspend`, { reason });
      await refreshAll();
      setSelectedUser(null);
    } catch (err) {
      alert(err?.response?.data?.detail || "Restrict failed");
    }
  };

  const reactivateUser = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/reactivate`);
      await refreshAll();
      setSelectedUser(null);
    } catch (err) {
      alert(err?.response?.data?.detail || "Reactivate failed");
    }
  };

  const resetPassword = async (userId) => {
    const newPassword = window.prompt("Enter new password, minimum 8 characters");
    if (!newPassword) return;

    if (newPassword.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }

    try {
      await api.post(`/api/admin/users/${userId}/reset-password`, {
        new_password: newPassword,
      });
      alert("Password reset successfully");
    } catch (err) {
      alert(err?.response?.data?.detail || "Password reset failed");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...form,
        experience_years: form.experience_years
          ? Number(form.experience_years)
          : null,
      };

      await api.post("/api/admin/users/register", payload);

      setShowCreateModal(false);
      setForm(emptyForm);
      await refreshAll();
      alert("User created successfully");
    } catch (err) {
      alert(err?.response?.data?.detail || "User creation failed");
    }
  };

  const badgeClass = (value) => {
    if (value === "approved") return "admin-badge admin-badge-green";
    if (value === "pending_admin_approval") return "admin-badge admin-badge-yellow";
    if (value === "rejected") return "admin-badge admin-badge-red";
    if (value === "suspended") return "admin-badge admin-badge-dark";
    return "admin-badge";
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Admin User Management</h1>
          <p>
            Register users, validate doctors/researchers, restrict accounts, and audit access.
          </p>
        </div>

        <button className="admin-primary-btn" onClick={() => setShowCreateModal(true)}>
          + Register User
        </button>
      </div>

      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <span>Total Users</span>
          <strong>{totalUsers}</strong>
        </div>
        <div className="admin-stat-card">
          <span>Pending Approvals</span>
          <strong>{pendingUsers.length}</strong>
        </div>
        <div className="admin-stat-card">
          <span>Current Page</span>
          <strong>{page} / {totalPages}</strong>
        </div>
      </div>

      <div className="admin-filter-card">
        <input
          placeholder="Search name, email, organization, speciality, license..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />

        <select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }}>
          <option value="">All Roles</option>
          <option value="user">User / Patient</option>
          <option value="doctor">Doctor</option>
          <option value="researcher">Researcher</option>
          <option value="admin">Admin</option>
        </select>

        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="approved">Approved</option>
          <option value="pending_admin_approval">Pending Approval</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Restricted / Suspended</option>
        </select>

        <button onClick={handleSearch}>Search</button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-table-card">
        {loading ? (
          <div className="admin-empty">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="admin-empty">No users found.</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Credentials</th>
                <th>Organization</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.name}</strong>
                    <small>{user.email}</small>
                  </td>

                  <td className="capitalize">{user.role}</td>

                  <td>
                    <span className={badgeClass(user.account_status)}>
                      {user.account_status}
                    </span>
                  </td>

                  <td>
                    {user.role === "doctor" ? (
                      <>
                        <div>{user.degree || "No degree"}</div>
                        <small>{user.speciality || "No speciality"}</small>
                        <small>License: {user.license_id || "N/A"}</small>
                      </>
                    ) : user.role === "researcher" ? (
                      <>
                        <div>{user.provider_type || "Researcher"}</div>
                        <small>{user.organization_name || "No organization"}</small>
                      </>
                    ) : (
                      <small>N/A</small>
                    )}
                  </td>

                  <td>{user.organization_name || "N/A"}</td>

                  <td>
                    {[user.city, user.state, user.country].filter(Boolean).join(", ") || "N/A"}
                  </td>

                  <td>
                    <div className="admin-actions">
                      <button onClick={() => setSelectedUser(user)}>View</button>

                      {user.account_status === "pending_admin_approval" && (
                        <>
                          <button className="green" onClick={() => approveUser(user.id)}>
                            Approve
                          </button>
                          <button className="red" onClick={() => rejectUser(user.id)}>
                            Reject
                          </button>
                        </>
                      )}

                      {user.account_status === "approved" && user.role !== "admin" && (
                        <button className="orange" onClick={() => suspendUser(user.id)}>
                          Restrict
                        </button>
                      )}

                      {["rejected", "suspended"].includes(user.account_status) && (
                        <button className="blue" onClick={() => reactivateUser(user.id)}>
                          Reactivate
                        </button>
                      )}

                      {user.role !== "admin" && (
                        <button className="dark" onClick={() => resetPassword(user.id)}>
                          Reset
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="admin-pagination">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>
        <span>Page {page} of {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </div>

      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onApprove={approveUser}
          onReject={rejectUser}
          onSuspend={suspendUser}
          onReactivate={reactivateUser}
          onResetPassword={resetPassword}
        />
      )}

      {showCreateModal && (
        <CreateUserModal
          form={form}
          setForm={setForm}
          onClose={() => setShowCreateModal(false)}
          onSubmit={createUser}
        />
      )}
    </div>
  );
}

function UserDetailsModal({
  user,
  onClose,
  onApprove,
  onReject,
  onSuspend,
  onReactivate,
  onResetPassword,
}) {
  return (
    <div className="admin-modal-backdrop">
      <div className="admin-modal large">
        <div className="admin-modal-header">
          <h2>User Details</h2>
          <button onClick={onClose}>×</button>
        </div>

        <div className="admin-details-grid">
          <Detail label="Name" value={user.name} />
          <Detail label="Email" value={user.email} />
          <Detail label="Role" value={user.role} />
          <Detail label="Status" value={user.account_status} />
          <Detail label="Active" value={user.is_active ? "Yes" : "No"} />
          <Detail label="Verified" value={user.is_verified ? "Yes" : "No"} />

          <Detail label="Degree" value={user.degree} />
          <Detail label="Speciality" value={user.speciality} />
          <Detail label="Sub Speciality" value={user.sub_speciality} />
          <Detail label="License ID" value={user.license_id} />
          <Detail label="Experience Years" value={user.experience_years} />

          <Detail label="Organization" value={user.organization_name} />
          <Detail label="Provider Type" value={user.provider_type} />
          <Detail label="Verification Document" value={user.verification_document_url} />
          <Detail label="Verification Notes" value={user.verification_notes} />

          <Detail label="City" value={user.city} />
          <Detail label="State" value={user.state} />
          <Detail label="Country" value={user.country} />

          <Detail label="Approved By" value={user.approved_by} />
          <Detail label="Approved At" value={user.approved_at} />
          <Detail label="Rejection / Restriction Reason" value={user.rejection_reason} />
          <Detail label="Created At" value={user.created_at} />
          <Detail label="Updated At" value={user.updated_at} />
        </div>

        <div className="admin-modal-actions">
          {user.account_status === "pending_admin_approval" && (
            <>
              <button className="green" onClick={() => onApprove(user.id)}>Approve</button>
              <button className="red" onClick={() => onReject(user.id)}>Reject</button>
            </>
          )}

          {user.account_status === "approved" && user.role !== "admin" && (
            <button className="orange" onClick={() => onSuspend(user.id)}>
              Restrict / Suspend
            </button>
          )}

          {["rejected", "suspended"].includes(user.account_status) && (
            <button className="blue" onClick={() => onReactivate(user.id)}>
              Reactivate
            </button>
          )}

          {user.role !== "admin" && (
            <button className="dark" onClick={() => onResetPassword(user.id)}>
              Reset Password
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ form, setForm, onClose, onSubmit }) {
  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="admin-modal-backdrop">
      <form className="admin-modal large" onSubmit={onSubmit}>
        <div className="admin-modal-header">
          <h2>Register User</h2>
          <button type="button" onClick={onClose}>×</button>
        </div>

        <div className="admin-form-grid">
          <input required placeholder="Full name" value={form.name} onChange={(e) => update("name", e.target.value)} />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => update("email", e.target.value)} />
          <input required type="password" placeholder="Password" value={form.password} onChange={(e) => update("password", e.target.value)} />

          <select value={form.role} onChange={(e) => update("role", e.target.value)}>
            <option value="user">User / Patient</option>
            <option value="doctor">Doctor</option>
            <option value="researcher">Researcher</option>
            <option value="admin">Admin</option>
          </select>

          <input placeholder="Degree" value={form.degree} onChange={(e) => update("degree", e.target.value)} />
          <input placeholder="Speciality" value={form.speciality} onChange={(e) => update("speciality", e.target.value)} />
          <input placeholder="Sub speciality" value={form.sub_speciality} onChange={(e) => update("sub_speciality", e.target.value)} />
          <input placeholder="License ID" value={form.license_id} onChange={(e) => update("license_id", e.target.value)} />
          <input type="number" placeholder="Experience years" value={form.experience_years} onChange={(e) => update("experience_years", e.target.value)} />

          <input placeholder="Organization name" value={form.organization_name} onChange={(e) => update("organization_name", e.target.value)} />
          <input placeholder="Provider type" value={form.provider_type} onChange={(e) => update("provider_type", e.target.value)} />

          <input placeholder="City" value={form.city} onChange={(e) => update("city", e.target.value)} />
          <input placeholder="State" value={form.state} onChange={(e) => update("state", e.target.value)} />
          <input placeholder="Country" value={form.country} onChange={(e) => update("country", e.target.value)} />

          <input placeholder="Verification document URL" value={form.verification_document_url} onChange={(e) => update("verification_document_url", e.target.value)} />
          <textarea placeholder="Verification notes" value={form.verification_notes} onChange={(e) => update("verification_notes", e.target.value)} />
        </div>

        <div className="admin-modal-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button className="admin-primary-btn" type="submit">Create User</button>
        </div>
      </form>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="admin-detail">
      <span>{label}</span>
      <strong>{value || "N/A"}</strong>
    </div>
  );
}